/*
  Atlas map — interactive MapLibre coverage map + sidebar. Ported verbatim from
  design-mocks/map-final-maplibre.html. The one structural change from the
  prototype: place data is read from the section's
  <script type="application/json" data-atlas-map-data> block (emitted from the
  map_places metaobject) instead of a hardcoded DATA blob, and the per-region /
  per-macro coverage stats (counts, product totals, bounding boxes) are DERIVED
  here from the places rather than shipped precomputed. The region -> 6-macro
  lookup is kept inline, exactly as in the prototype.

  PRODUCTION TODO: self-host vector tiles + glyphs. The style below points at
  OpenFreeMap (https://tiles.openfreemap.org/planet + their font endpoint),
  which is free and key-less but asks commercial / heavy sites to self-host or
  sponsor. Before launch, stand up our own tile + glyph host (self-hosted
  OpenFreeMap planet, or MapTiler / Protomaps PMTiles on our CDN) and swap the
  `glyphs` and `sources.ofm.url` URLs below.
*/
(function () {
  "use strict";

  /* region -> macro-zone lookup — inline, exactly as in the prototype */
  var R2M = {
    "New England Coast": "US East Coast",
    "Mid-Atlantic Coast": "US East Coast",
    "Chesapeake Bay": "US East Coast",
    "New Jersey Coast": "US East Coast",
    "Long Island & NY Harbor": "US East Coast",
    "Southeast Coast": "US East Coast",
    "Florida Atlantic": "Florida & Gulf",
    "Florida Gulf": "Florida & Gulf",
    "Florida Keys": "Florida & Gulf",
    "Gulf Coast": "Florida & Gulf",
    "Midwest Inland Lakes": "Great Lakes & Inland",
    "Northeast Inland Lakes": "Great Lakes & Inland",
    "Great Lakes": "Great Lakes & Inland",
    "Lake Erie": "Great Lakes & Inland",
    "Lake Huron": "Great Lakes & Inland",
    "Lake Michigan": "Great Lakes & Inland",
    "Lake Ontario": "Great Lakes & Inland",
    "Lake Superior": "Great Lakes & Inland",
    "Rivers & Canyons": "Great Lakes & Inland",
    "California Coast": "West & Mountain",
    "Pacific Northwest": "West & Mountain",
    "Western Inland Lakes": "West & Mountain",
    "Texas Inland Lakes": "West & Mountain",
    Alaska: "West & Mountain",
    Hawaii: "West & Mountain",
    Caribbean: "Caribbean & Mexico",
    Bahamas: "Caribbean & Mexico",
    "Mexico & Central America": "Caribbean & Mexico",
    Europe: "Europe & Beyond",
    Canada: "Europe & Beyond",
    "Other International": "Europe & Beyond",
  };
  var MACRO_ORDER = [
    "US East Coast",
    "Florida & Gulf",
    "Great Lakes & Inland",
    "West & Mountain",
    "Caribbean & Mexico",
    "Europe & Beyond",
  ];

  /* ---------- helpers ---------- */
  function fmtCoords(lat, lng) {
    function part(v, pos, neg) {
      var h = v >= 0 ? pos : neg,
        a = Math.abs(v),
        d = Math.floor(a),
        m = Math.round((a - d) * 60);
      if (m === 60) {
        d++;
        m = 0;
      }
      return d + "°" + (m < 10 ? "0" : "") + m + "′" + h;
    }
    return part(lat, "N", "S") + " · " + part(lng, "E", "W");
  }
  function haversine(a, b) {
    // miles - the store and its customers are almost entirely US
    var R = 3958.8,
      toR = Math.PI / 180;
    var dLat = (b.lat - a.lat) * toR,
      dLng = (b.lng - a.lng) * toR;
    var la1 = a.lat * toR,
      la2 = b.lat * toR;
    var x =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(x));
  }
  function charts(n) {
    return n === 1 ? "1 chart" : n + " charts";
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }
  function extendBbox(acc, lng, lat) {
    if (!acc) return [lng, lat, lng, lat];
    return [
      Math.min(acc[0], lng),
      Math.min(acc[1], lat),
      Math.max(acc[2], lng),
      Math.max(acc[3], lat),
    ];
  }
  function unionBbox(acc, b) {
    if (!acc) return b.slice();
    return [
      Math.min(acc[0], b[0]),
      Math.min(acc[1], b[1]),
      Math.max(acc[2], b[2]),
      Math.max(acc[3], b[3]),
    ];
  }

  function initRoot(root) {
    var dataEl = root.querySelector("[data-atlas-map-data]");
    if (!dataEl || typeof maplibregl === "undefined") return;

    var raw;
    try {
      raw = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }
    if (!raw || !raw.length) return;

    /* ---------- authored place -> region map ----------
       Region isn't a store field — it's the authored 31-region / 6-macro
       taxonomy (snippets/atlas-map-region-data.liquid). Join it here by place
       name (trimmed, case-insensitive) so the coverage accordion has zones. */
    var REGION_MAP = {};
    var regionEl = root.querySelector("[data-atlas-region-map]");
    if (regionEl) {
      try {
        var rm = JSON.parse(regionEl.textContent);
        Object.keys(rm).forEach(function (k) {
          REGION_MAP[k.trim().toLowerCase()] = rm[k];
        });
      } catch (e) {}
    }
    function regionFor(name, fallback) {
      var key = (name || "").trim().toLowerCase();
      return REGION_MAP[key] || fallback || "";
    }

    /* ---------- normalize places ---------- */
    var PLACES = raw.map(function (p, i) {
      return {
        id: i,
        name: p.name,
        lat: +p.lat,
        lng: +p.lng,
        region: regionFor(p.name, p.region),
        products: +p.count || 0,
        url: p.url || "#",
      };
    });
    var TOTAL_WATERS = PLACES.length;
    var TOTAL_CHARTS = PLACES.reduce(function (s, p) {
      return s + p.products;
    }, 0);

    /* ---------- derive per-region + per-macro stats ---------- */
    var REGIONS = {};
    PLACES.forEach(function (p) {
      if (!p.region) return;
      var r = REGIONS[p.region];
      if (!r) {
        r = REGIONS[p.region] = { count: 0, prod: 0, bbox: null };
      }
      r.count += 1;
      r.prod += p.products;
      r.bbox = extendBbox(r.bbox, p.lng, p.lat);
    });

    var macroRegions = {};
    MACRO_ORDER.forEach(function (m) {
      macroRegions[m] = [];
    });
    Object.keys(REGIONS).forEach(function (rn) {
      var m = R2M[rn];
      if (m && macroRegions[m]) macroRegions[m].push(rn);
    });
    MACRO_ORDER.forEach(function (m) {
      macroRegions[m].sort(function (a, b) {
        return REGIONS[b].count - REGIONS[a].count;
      });
    });

    var MACROS = {};
    MACRO_ORDER.forEach(function (m) {
      MACROS[m] = { count: 0, prod: 0, bbox: null };
    });
    Object.keys(REGIONS).forEach(function (rn) {
      var m = R2M[rn];
      if (!m || !MACROS[m]) return;
      var r = REGIONS[rn];
      MACROS[m].count += r.count;
      MACROS[m].prod += r.prod;
      MACROS[m].bbox = unionBbox(MACROS[m].bbox, r.bbox);
    });

    function adjacent(place, n) {
      return PLACES.filter(function (p) {
        return p.id !== place.id;
      })
        .map(function (p) {
          return { p: p, d: haversine(place, p) };
        })
        .sort(function (a, b) {
          return a.d - b.d;
        })
        .slice(0, n);
    }

    /* ---------- map style (dark Atlas restyle of OpenFreeMap / OpenMapTiles) ---------- */
    var LAND = "#132430",
      WATER = "#0a141d",
      LANDLINE = "rgba(168,130,63,.16)",
      ADMIN = "rgba(207,169,97,.22)";
    var style = {
      version: 8,
      glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
      sources: {
        ofm: {
          type: "vector",
          url: "https://tiles.openfreemap.org/planet",
          attribution:
            '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> · ' +
            '<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap</a>',
        },
      },
      layers: [
        { id: "bg", type: "background", paint: { "background-color": LAND } },
        {
          id: "land-ice",
          type: "fill",
          source: "ofm",
          "source-layer": "landcover",
          filter: ["==", "class", "ice"],
          paint: { "fill-color": "#1a2c38", "fill-opacity": 0.5 },
        },
        {
          id: "water",
          type: "fill",
          source: "ofm",
          "source-layer": "water",
          filter: ["!=", "brunnel", "tunnel"],
          paint: { "fill-color": WATER, "fill-outline-color": LANDLINE },
        },
        {
          id: "waterway",
          type: "line",
          source: "ofm",
          "source-layer": "waterway",
          paint: {
            "line-color": WATER,
            "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.4, 12, 1.6],
            "line-opacity": 0.7,
          },
        },
        {
          id: "coast-glow",
          type: "line",
          source: "ofm",
          "source-layer": "water",
          paint: {
            "line-color": "rgba(207,169,97,.20)",
            "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.3, 8, 1],
          },
        },
        {
          id: "boundary-country",
          type: "line",
          source: "ofm",
          "source-layer": "boundary",
          filter: ["<=", "admin_level", 2],
          paint: {
            "line-color": ADMIN,
            "line-width": ["interpolate", ["linear"], ["zoom"], 2, 0.5, 8, 1.4],
            "line-dasharray": [2, 1.6],
          },
        },
        {
          id: "boundary-state",
          type: "line",
          source: "ofm",
          "source-layer": "boundary",
          filter: ["all", [">=", "admin_level", 3], ["<=", "admin_level", 4]],
          paint: {
            "line-color": "rgba(207,169,97,.12)",
            "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.3, 9, 0.9],
            "line-dasharray": [1.5, 2],
          },
        },
      ],
    };

    /* Respect prefers-reduced-motion for fly / zoom animations. */
    var reduceMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    function dur(ms) {
      return reduceMotion ? 0 : ms;
    }

    /* ---------- init map ---------- */
    var mapEl = root.querySelector(".atlas-map__canvas");
    var map = new maplibregl.Map({
      container: mapEl,
      style: style,
      center: [-92, 39],
      zoom: 3.4,
      minZoom: 1.6,
      maxZoom: 15,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    var geojson = {
      type: "FeatureCollection",
      features: PLACES.map(function (p) {
        return {
          type: "Feature",
          id: p.id,
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
          properties: { pid: p.id, name: p.name, products: p.products, region: p.region },
        };
      }),
    };

    var selectedId = null;
    /* Phones only. On a narrow screen the sidebar sits below the map, and
       customers were missing it entirely — they tap a pin, nothing visibly
       happens, and the detail is off-screen. So on phones the detail opens as
       a popup anchored to the pin itself. Desktop keeps the sidebar. */
    function isPhone() {
      return window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
    }
    var detailPopup = null;
    function closeDetailPopup() {
      if (detailPopup) {
        detailPopup.remove();
        detailPopup = null;
      }
    }

    /* ---------- sidebar handles ---------- */
    var panel = root.querySelector(".atlas-map__panel");
    var searchEl = root.querySelector(".atlas-map__search");
    var searchBox = root.querySelector(".atlas-map__searchbox");
    var clrBtn = root.querySelector(".atlas-map__clr");
    var readoutEl = root.querySelector(".atlas-map__readout");
    var openMacros = {};

    map.on("load", function () {
      map.addSource("waters", {
        type: "geojson",
        data: geojson,
        cluster: true,
        clusterRadius: 46,
        clusterMaxZoom: 8,
        clusterProperties: { prod: ["+", ["get", "products"]] },
      });

      /* cluster bubble */
      map.addLayer(
        {
          id: "cluster-halo",
          type: "circle",
          source: "waters",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": "rgba(207,169,97,.14)",
            "circle-radius": ["step", ["get", "point_count"], 26, 5, 31, 12, 37, 30, 44],
            "circle-opacity": 0.9,
          },
        },
        undefined
      );
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "waters",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#a8823f",
          "circle-radius": ["step", ["get", "point_count"], 17, 5, 21, 12, 26, 30, 32],
          "circle-stroke-color": "#cfa961",
          "circle-stroke-width": 2,
          "circle-opacity": 0.94,
        },
      });
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "waters",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["Noto Sans Bold"],
          "text-size": 13,
        },
        paint: { "text-color": "#0e1b26" },
      });

      /* unclustered pins */
      map.addLayer({
        id: "pin-glow",
        type: "circle",
        source: "waters",
        filter: ["!", ["has", "point_count"]],
        paint: { "circle-color": "rgba(207,169,97,.16)", "circle-radius": 13 },
      });
      map.addLayer({
        id: "pins",
        type: "circle",
        source: "waters",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#cfa961",
          "circle-radius": 7,
          "circle-stroke-color": "#0e1b26",
          "circle-stroke-width": 1.5,
        },
      });
      /* invisible, generous hit target so a waypoint is easy to tap/click; it
         grows with zoom so the reach roughly extends toward the engraved label
         (the label layer is also made clickable below). */
      map.addLayer({
        id: "pin-hit",
        type: "circle",
        source: "waters",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#000",
          "circle-opacity": 0,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 16, 7, 22, 10, 30],
        },
      });
      /* selected highlight (filter swapped on select) */
      map.addLayer({
        id: "pin-selected",
        type: "circle",
        source: "waters",
        filter: ["==", ["get", "pid"], -1],
        paint: {
          "circle-color": "#f7f2e9",
          "circle-radius": 9,
          "circle-stroke-color": "#cfa961",
          "circle-stroke-width": 4,
        },
      });

      /* engraved chart labels — individual (unclustered) pins only, and only
         once zoomed in close enough that the field isn't dense. Collision
         detection keeps them from piling up.
         NOTE: map glyphs must come from the tile host's font endpoint, which
         serves the Noto family, not IBM Plex Mono — so these render in Noto
         Italic (a restrained, chart-like face) rather than Plex Mono. When we
         self-host glyphs (production TODO above) we can ship Plex Mono and swap
         the "text-font" below to ["IBM Plex Mono Regular"]. */
      map.addLayer({
        id: "pin-labels",
        type: "symbol",
        source: "waters",
        filter: ["!", ["has", "point_count"]],
        minzoom: 6.5,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Italic"],
          "text-size": 11,
          "text-offset": [1.6, 0.1],
          "text-anchor": "left",
          "text-justify": "left",
          "text-max-width": 9,
          "text-letter-spacing": 0.02,
          "text-padding": 4,
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: {
          "text-color": "#e8dcc0",
          "text-halo-color": "#0a141d",
          "text-halo-width": 1.1,
          "text-halo-blur": 0.4,
          "text-opacity": ["interpolate", ["linear"], ["zoom"], 6.4, 0, 7.1, 0.92],
        },
      });

      /* interactions */
      map.on("click", "clusters", function (e) {
        var f = e.features[0],
          cid = f.properties.cluster_id;
        map
          .getSource("waters")
          .getClusterExpansionZoom(cid)
          .then(function (z) {
            map.easeTo({ center: f.geometry.coordinates, zoom: z, duration: dur(600) });
          });
      });
      /* select from the pin, its wider hit target, or its engraved label */
      ["pins", "pin-hit", "pin-labels"].forEach(function (l) {
        map.on("click", l, function (e) {
          selectPlace(e.features[0].properties.pid, true, false);
        });
      });
      ["clusters", "pins", "pin-hit", "pin-labels"].forEach(function (l) {
        map.on("mouseenter", l, function () {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", l, function () {
          map.getCanvas().style.cursor = "";
        });
      });

      updateReadout();
      var ph = root.querySelector("[data-atlas-map-placeholder]");
      if (ph) {
        ph.classList.add("is-gone");
        setTimeout(function () {
          ph.style.display = "none";
        }, 700);
      }

      /* Interaction gate: hold pan/zoom until the visitor opts in, so scrolling
         past the map (or swiping over it on a phone) moves the page instead of
         the map. One click hands control over. */
      var gate = root.querySelector("[data-atlas-map-gate]");
      if (gate) {
        var handlers = [
          map.scrollZoom,
          map.dragPan,
          map.boxZoom,
          map.doubleClickZoom,
          map.touchZoomRotate,
          map.keyboard,
          map.dragRotate,
        ];
        handlers.forEach(function (h) {
          if (h && h.disable) h.disable();
        });
        function openGate() {
          handlers.forEach(function (h) {
            if (h && h.enable) h.enable();
          });
          gate.classList.add("is-gone");
          setTimeout(function () {
            gate.style.display = "none";
          }, 400);
        }
        gate.addEventListener("click", openGate);

        /* Reaching for the search box is already opting in — leaving "click to
           explore" sitting over the map while someone types reads as though
           the search isn't wired up. Selecting a result pans the map, so the
           gate has to be open by then anyway. */
        if (searchEl) {
          searchEl.addEventListener("focus", openGate, { once: true });
        }
      }
    });

    map.on("move", updateReadout);
    var hintGone = false;
    map.on("movestart", function () {
      if (hintGone) return;
      hintGone = true;
      var h = root.querySelector(".atlas-map__hint");
      if (!h) return;
      h.classList.add("is-gone");
      setTimeout(function () {
        h.style.display = "none";
      }, 600);
    });
    function updateReadout() {
      if (!readoutEl) return;
      var c = map.getCenter();
      readoutEl.textContent = fmtCoords(c.lat, c.lng) + "  ·  z" + map.getZoom().toFixed(1);
    }

    /* ---------- sidebar rendering ---------- */
    function flyToBbox(bbox, pad) {
      map.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding: pad || 70, duration: dur(900), maxZoom: 9 }
      );
    }

    function renderCoverage() {
      root.classList.remove("is-searching");
      searchEl.setAttribute("aria-expanded", "false");
      var h = "";
      h += '<div class="seclabel">Coverage · The whole atlas</div>';
      h +=
        '<div class="stat">' +
        '<div><div class="n">' +
        TOTAL_WATERS +
        '</div><div class="l">Waters charted</div></div>' +
        '<div><div class="n">' +
        TOTAL_CHARTS +
        '</div><div class="l">Charts available</div></div>' +
        "</div>";
      h += '<div class="seclabel">Regions · ' + MACRO_ORDER.length + " zones</div>";
      h += '<div class="macros">';
      MACRO_ORDER.forEach(function (m, mi) {
        if (!macroRegions[m].length) return;
        var meta = MACROS[m],
          isOpen = !!openMacros[m];
        h += '<div class="macro' + (isOpen ? " open" : "") + '" data-macro="' + esc(m) + '">';
        h += '<div class="row">';
        h +=
          '<button class="rowmain" data-toggle="' +
          mi +
          '" aria-expanded="' +
          isOpen +
          '" style="display:flex;align-items:center;gap:10px;flex:1 1 auto;text-align:left">' +
          '<span class="caret">▶</span>' +
          '<span class="nm">' +
          esc(m) +
          "</span>" +
          '<span class="cnt">' +
          meta.count +
          " · " +
          meta.prod +
          "</span>" +
          "</button>";
        h +=
          '<button class="fly" data-flymacro="' +
          mi +
          '" aria-label="Fly to ' +
          esc(m) +
          '">Fly</button>';
        h += "</div>";
        h += '<div class="regions">';
        macroRegions[m].forEach(function (rn) {
          var r = REGIONS[rn];
          h +=
            '<button data-region="' +
            esc(rn) +
            '">' +
            '<span class="rn">' +
            esc(rn) +
            "</span>" +
            '<span class="rc">' +
            r.count +
            " · " +
            r.prod +
            "</span>" +
            "</button>";
        });
        h += "</div></div>";
      });
      h += "</div>";
      h +=
        '<div class="muted" style="margin-top:16px;font-family:\'IBM Plex Mono\',monospace;font-size:10px;letter-spacing:.1em">' +
        "waters · charts &nbsp;—&nbsp; tap a zone to expand, “Fly” to zoom there</div>";
      panel.innerHTML = h;

      panel.querySelectorAll("[data-toggle]").forEach(function (b) {
        b.addEventListener("click", function () {
          var m = b.closest(".macro").getAttribute("data-macro");
          openMacros[m] = !openMacros[m];
          b.closest(".macro").classList.toggle("open", openMacros[m]);
          b.setAttribute("aria-expanded", openMacros[m]);
        });
      });
      panel.querySelectorAll("[data-flymacro]").forEach(function (b) {
        b.addEventListener("click", function () {
          var m = b.closest(".macro").getAttribute("data-macro");
          flyToBbox(MACROS[m].bbox, 90);
        });
      });
      panel.querySelectorAll("[data-region]").forEach(function (b) {
        b.addEventListener("click", function () {
          var rn = b.getAttribute("data-region");
          flyToBbox(REGIONS[rn].bbox, 70);
        });
      });
    }

    /* ----- detail panel ----- */
    function renderDetail(place) {
      root.classList.remove("is-searching");
      var adj = adjacent(place, 5);
      var h = '<div class="detail">';
      h += '<button class="backlink" data-back>‹ Back to coverage</button>';
      h += '<div class="region-tag">' + esc(place.region) + "</div>";
      h += "<h2>" + esc(place.name) + "</h2>";
      h += '<div class="coords">' + fmtCoords(place.lat, place.lng) + "</div>";
      h +=
        '<a class="charts-stat" href="' +
        esc(place.url) +
        '"><span class="n">' +
        place.products +
        "</span>" +
        '<span class="l">' +
        (place.products === 1 ? "chart" : "charts") +
        "<br>available</span></a>";
      h +=
        '<div class="line">Hand-drawn from real hydrographic survey data and laser-cut in ' +
        "layered wood — the depth contours you’d see on the chart, raised off the mantel.</div>";
      h += '<div class="seclabel">Adjacent waters</div>';
      h += '<div class="adjlist">';
      adj.forEach(function (a) {
        h +=
          '<button data-pid="' +
          a.p.id +
          '">' +
          '<span class="an">' +
          esc(a.p.name) +
          "</span>" +
          '<span class="ad">' +
          Math.round(a.d) +
          " mi · <b>" +
          a.p.products +
          "</b></span>" +
          "</button>";
      });
      h += "</div>";
      h += '<a class="cta" href="' + esc(place.url) + '" role="button">View charts</a>';
      h += "</div>";
      panel.innerHTML = h;

      panel.querySelector("[data-back]").addEventListener("click", clearSelection);
      panel.querySelectorAll("[data-pid]").forEach(function (b) {
        b.addEventListener("click", function () {
          selectPlace(+b.getAttribute("data-pid"), true, false);
        });
      });

      if (isPhone()) openDetailPopup(place, adj);
    }

    /* Carries the three nearest waters as well as the headline facts. That
       makes the card ~250px, more than the half-map a centred pin leaves
       above it — so selectPlace drops the pin below centre on phones to open
       up the space (see the flyTo offset). */
    function openDetailPopup(place, adj) {
      closeDetailPopup();
      var h = '<div class="atlas-pop">';
      h += '<div class="atlas-pop__region">' + esc(place.region) + "</div>";
      h += '<h3 class="atlas-pop__name">' + esc(place.name) + "</h3>";
      h += '<div class="atlas-pop__coords">' + fmtCoords(place.lat, place.lng) + "</div>";
      h +=
        '<a class="atlas-pop__cta" href="' +
        esc(place.url) +
        '"><b>' +
        place.products +
        "</b> " +
        (place.products === 1 ? "chart" : "charts") +
        " &rsaquo;</a>";
      if (adj && adj.length) {
        h += '<div class="atlas-pop__lbl">Nearby</div><div class="atlas-pop__adj">';
        adj.slice(0, 3).forEach(function (a) {
          h +=
            '<button data-pid="' +
            a.p.id +
            '"><span>' +
            esc(a.p.name) +
            "</span><i>" +
            Math.round(a.d) +
            " mi</i></button>";
        });
        h += "</div>";
      }
      h += "</div>";

      detailPopup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: false,
        maxWidth: "82vw",
        offset: 16,
        // selectPlace centres the pin, so sitting the card above it keeps the
        // whole thing inside the map; letting maplibre choose put it below the
        // pin and ran it off the bottom edge.
        anchor: "bottom",
        className: "atlas-mappop",
      })
        .setLngLat([place.lng, place.lat])
        .setHTML(h)
        .addTo(map);

      detailPopup
        .getElement()
        .querySelectorAll("[data-pid]")
        .forEach(function (b) {
          b.addEventListener("click", function () {
            selectPlace(+b.getAttribute("data-pid"), true, false);
          });
        });
      detailPopup.on("close", function () {
        detailPopup = null;
      });
    }

    /* ---------- selection ---------- */
    function selectPlace(id, fly, fromSearch) {
      var place = PLACES[id];
      selectedId = id;
      if (map.getLayer("pin-selected")) map.setFilter("pin-selected", ["==", ["get", "pid"], id]);
      if (fly) {
        map.flyTo({
          center: [place.lng, place.lat],
          // Phones open a popup above the pin, so bias the pin downward:
          // centred, the card would run off the top of the map.
          offset: isPhone() ? [0, 90] : [0, 0],
          zoom: Math.max(map.getZoom(), 8.5),
          duration: dur(1100),
          essential: true,
        });
      }
      if (!fromSearch) {
        searchEl.value = "";
        searchBox.classList.remove("has-val");
      }
      renderDetail(place);
      panel.scrollTop = 0;
    }
    function clearSelection() {
      selectedId = null;
      closeDetailPopup();
      if (map.getLayer("pin-selected")) map.setFilter("pin-selected", ["==", ["get", "pid"], -1]);
      searchEl.value = "";
      searchBox.classList.remove("has-val");
      renderCoverage();
      searchEl.focus();
    }

    /* ---------- search ---------- */
    var activeIdx = -1,
      curResults = [];
    function runSearch(q) {
      q = q.trim().toLowerCase();
      searchBox.classList.toggle("has-val", q.length > 0);
      if (!q) {
        activeIdx = -1;
        curResults = [];
        searchEl.setAttribute("aria-expanded", "false");
        if (selectedId != null) {
          renderDetail(PLACES[selectedId]);
        } else {
          renderCoverage();
        }
        return;
      }
      curResults = PLACES.filter(function (p) {
        return p.name.toLowerCase().indexOf(q) > -1 || p.region.toLowerCase().indexOf(q) > -1;
      })
        .sort(function (a, b) {
          var ai = a.name.toLowerCase().indexOf(q),
            bi = b.name.toLowerCase().indexOf(q);
          if (ai !== bi) return ai - bi;
          return a.name.localeCompare(b.name);
        })
        .slice(0, 40);
      activeIdx = curResults.length ? 0 : -1;
      renderResults(q);
    }
    function renderResults(q) {
      searchEl.setAttribute("aria-expanded", curResults.length > 0 ? "true" : "false");
      // lets the stylesheet float the results under the overlaid search box
      root.classList.add("is-searching");
      if (!curResults.length) {
        panel.innerHTML =
          '<div class="noresult">No water matches “' +
          esc(q) +
          "”. Try a lake, bay, or island name.</div>";
        return;
      }
      var h =
        '<div class="seclabel">' +
        curResults.length +
        " match" +
        (curResults.length === 1 ? "" : "es") +
        '</div><div class="results" role="listbox">';
      curResults.forEach(function (p, i) {
        var idx = p.name.toLowerCase().indexOf(q);
        var nm =
          idx < 0
            ? esc(p.name)
            : esc(p.name.slice(0, idx)) +
              "<b>" +
              esc(p.name.slice(idx, idx + q.length)) +
              "</b>" +
              esc(p.name.slice(idx + q.length));
        h +=
          '<button role="option" data-idx="' +
          i +
          '" class="' +
          (i === activeIdx ? "active" : "") +
          '" aria-selected="' +
          (i === activeIdx) +
          '">' +
          '<span class="rr-nm">' +
          nm +
          "</span>" +
          '<span class="rr-meta">' +
          esc(p.region) +
          " · " +
          charts(p.products) +
          "</span>" +
          "</button>";
      });
      h += "</div>";
      panel.innerHTML = h;
      panel.querySelectorAll("[data-idx]").forEach(function (b) {
        b.addEventListener("click", function () {
          selectPlace(curResults[+b.getAttribute("data-idx")].id, true, false);
        });
      });
    }
    function moveActive(delta) {
      if (!curResults.length) return;
      activeIdx = (activeIdx + delta + curResults.length) % curResults.length;
      var btns = panel.querySelectorAll("[data-idx]");
      btns.forEach(function (b, i) {
        var on = i === activeIdx;
        b.classList.toggle("active", on);
        b.setAttribute("aria-selected", on);
        if (on) b.scrollIntoView({ block: "nearest" });
      });
    }

    searchEl.addEventListener("input", function () {
      runSearch(searchEl.value);
    });
    searchEl.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveActive(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        moveActive(-1);
      } else if (e.key === "Enter") {
        if (activeIdx >= 0 && curResults[activeIdx]) {
          e.preventDefault();
          selectPlace(curResults[activeIdx].id, true, false);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (searchEl.value) {
          searchEl.value = "";
          runSearch("");
        } else if (selectedId != null) {
          clearSelection();
        }
      }
    });
    clrBtn.addEventListener("click", function () {
      searchEl.value = "";
      runSearch("");
      searchEl.focus();
    });

    /* global Esc clears selection even when map focused */
    root.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && document.activeElement !== searchEl && selectedId != null) {
        clearSelection();
      }
    });

    /* Paint the coverage sidebar immediately — it needs only the place data,
       not loaded tiles — so the panel is useful the moment the library lands. */
    renderCoverage();
  }

  /* ---------- lazy loader: only fetch + init MapLibre near the viewport ----------
     Keeps the ~1 MB maplibre-gl.js off the homepage's initial load / LCP path.
     The vendor JS + CSS URLs are handed in on the section root as data-attrs. */
  var maplibrePromise = null;
  function loadMaplibre(root) {
    if (typeof maplibregl !== "undefined") return Promise.resolve();
    if (maplibrePromise) return maplibrePromise;
    var jsUrl = root.getAttribute("data-atlas-maplibre-js");
    var cssUrl = root.getAttribute("data-atlas-maplibre-css");
    maplibrePromise = new Promise(function (resolve, reject) {
      if (!jsUrl) {
        reject();
        return;
      }
      if (cssUrl && !document.querySelector('link[data-atlas-maplibre-css="1"]')) {
        var link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = cssUrl;
        link.setAttribute("data-atlas-maplibre-css", "1");
        document.head.appendChild(link);
      }
      var s = document.createElement("script");
      s.src = jsUrl;
      s.async = true;
      s.onload = function () {
        resolve();
      };
      s.onerror = function () {
        reject();
      };
      document.head.appendChild(s);
    });
    return maplibrePromise;
  }

  function startRoot(root) {
    if (root.getAttribute("data-atlas-map-ready") === "1") return;
    root.setAttribute("data-atlas-map-ready", "1");
    loadMaplibre(root).then(
      function () {
        initRoot(root);
      },
      function () {
        /* library failed to load — leave the static placeholder in place */
      }
    );
  }

  /* Deep link (#atlas). The band is the LAST section on the homepage, so the
     browser's initial fragment scroll happens while the hero and product
     imagery above it may still be settling — every pixel those add pushes the
     band further down and leaves the visitor landing short of it. Re-run the
     scroll once at load, unless the visitor has already taken over. */
  function honourDeepLink() {
    if (window.location.hash !== "#atlas") return;
    var target = document.getElementById("atlas");
    if (!target) return;

    var taken = false;
    var release = function () {
      taken = true;
    };
    ["wheel", "touchstart", "keydown"].forEach(function (evt) {
      window.addEventListener(evt, release, { passive: true, once: true });
    });

    var settle = function () {
      if (!taken) target.scrollIntoView();
    };
    if (document.readyState === "complete") settle();
    else window.addEventListener("load", settle, { once: true });
  }

  function boot() {
    honourDeepLink();
    var roots = document.querySelectorAll(".atlas-map[data-atlas-map]");
    roots.forEach(function (root) {
      if (!("IntersectionObserver" in window)) {
        startRoot(root);
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              io.disconnect();
              startRoot(root);
            }
          });
        },
        { rootMargin: "500px 0px" }
      );
      io.observe(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
